import { useEffect, useLayoutEffect, useState } from "react";
import { X, ArrowRight, ArrowLeft, PawPrint } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

// Tour guiado de bienvenida: la primera vez que alguien entra a su negocio
// (Usuario.tourCompletado = false), va resaltando uno por uno los enlaces
// principales del Sidebar (identificados con el atributo data-tour="...",
// ver components/layout/Sidebar.tsx) con un recuadro y una tarjeta
// explicando qué es cada sección. Se puede saltar en cualquier momento, y
// también se puede volver a mostrar manualmente (ver reiniciarTour en
// AuthContext) desde donde se decida exponerlo en la interfaz.
const PASOS = [
  { seccion: "dashboard", titulo: "Panel", texto: "Aquí ves de un vistazo lo más importante del día: citas próximas, recordatorios pendientes y alertas de tu negocio." },
  { seccion: "clientes", titulo: "Clientes", texto: "Da de alta a los tutores de las mascotas: nombre, teléfono, correo y domicilio. Desde aquí administras a todos tus clientes." },
  { seccion: "mascotas", titulo: "Mascotas", texto: "El expediente clínico de cada mascota: consultas, vacunas, recetas y documentos, todo en una sola línea de tiempo." },
  { seccion: "agenda", titulo: "Agenda", texto: "Tu calendario de citas por día y semana. Agenda cualquier servicio en segundos desde el catálogo de tu negocio." },
  { seccion: "inventario", titulo: "Productos y Servicios", texto: "Controla tu stock de productos con alertas de caducidad y de stock bajo, o carga tu catálogo completo por CSV." },
  { seccion: "usuarios", titulo: "Usuarios", texto: "Da de alta a tu equipo (veterinarios, recepción, etc.) y controla qué puede ver o editar cada quien." },
  { seccion: "configuracion", titulo: "Configuración", texto: "Aquí personalizas tu negocio: logo, color de marca, datos fiscales, roles y permisos, y mucho más." },
];

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function medir(seccion: string): Rect | null {
  const el = document.querySelector(`[data-tour="${seccion}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export function TourGuiado() {
  const { completarTour } = useAuth();
  const [pasosValidos, setPasosValidos] = useState<typeof PASOS | null>(null);
  const [indice, setIndice] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  // Filtra a solo las secciones que sí existen en el Sidebar en este momento
  // (un módulo puede estar desactivado, oculto por permisos, o el Sidebar
  // completo no se muestra en pantallas pequeñas). Si no queda ninguna, no
  // hay nada que resaltar y el tour simplemente no se muestra.
  useEffect(() => {
    const validos = PASOS.filter((p) => medir(p.seccion));
    setPasosValidos(validos);
  }, []);

  useLayoutEffect(() => {
    if (!pasosValidos || pasosValidos.length === 0) return;
    function actualizar() {
      setRect(medir(pasosValidos![indice].seccion));
    }
    actualizar();
    window.addEventListener("resize", actualizar);
    return () => window.removeEventListener("resize", actualizar);
  }, [pasosValidos, indice]);

  if (!pasosValidos || pasosValidos.length === 0 || !rect) return null;

  const paso = pasosValidos[indice];
  const esUltimo = indice === pasosValidos.length - 1;
  const margen = 8;

  // Tarjeta a la derecha del elemento resaltado (el Sidebar vive a la
  // izquierda de la pantalla), verticalmente centrada con el recuadro.
  const tarjetaTop = Math.min(Math.max(rect.top - 20, 16), window.innerHeight - 260);
  const tarjetaLeft = rect.left + rect.width + 20;

  function siguiente() {
    if (esUltimo) {
      completarTour();
    } else {
      setIndice((i) => i + 1);
    }
  }

  function anterior() {
    setIndice((i) => Math.max(0, i - 1));
  }

  return (
    <div className="fixed inset-0 z-[100]">
      {/* Cuatro paneles oscuros que rodean el recuadro resaltado (efecto "spotlight") */}
      <div className="fixed bg-slate-900/60" style={{ top: 0, left: 0, right: 0, height: Math.max(rect.top - margen, 0) }} />
      <div
        className="fixed bg-slate-900/60"
        style={{ top: rect.top + rect.height + margen, left: 0, right: 0, bottom: 0 }}
      />
      <div
        className="fixed bg-slate-900/60"
        style={{ top: Math.max(rect.top - margen, 0), left: 0, width: Math.max(rect.left - margen, 0), height: rect.height + margen * 2 }}
      />
      <div
        className="fixed bg-slate-900/60"
        style={{
          top: Math.max(rect.top - margen, 0),
          left: rect.left + rect.width + margen,
          right: 0,
          height: rect.height + margen * 2,
        }}
      />
      {/* Recuadro resaltado */}
      <div
        className="pointer-events-none fixed rounded-xl ring-2 ring-teal-400 ring-offset-2"
        style={{ top: rect.top - margen, left: rect.left - margen, width: rect.width + margen * 2, height: rect.height + margen * 2 }}
      />

      {/* Tarjeta explicativa */}
      <div
        className="fixed w-72 rounded-2xl bg-white p-5 shadow-2xl"
        style={{ top: tarjetaTop, left: Math.min(tarjetaLeft, window.innerWidth - 300) }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-50 text-teal-600">
              <PawPrint size={16} />
            </span>
            <p className="font-semibold text-slate-800">{paso.titulo}</p>
          </div>
          <button onClick={completarTour} className="text-slate-300 hover:text-slate-500" aria-label="Saltar tour">
            <X size={18} />
          </button>
        </div>
        <p className="mt-3 text-sm text-slate-500">{paso.texto}</p>
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-slate-400">
            {indice + 1} de {pasosValidos.length}
          </p>
          <div className="flex items-center gap-2">
            {indice > 0 && (
              <button onClick={anterior} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-600" aria-label="Anterior">
                <ArrowLeft size={16} />
              </button>
            )}
            <button
              onClick={siguiente}
              className="inline-flex items-center gap-1 rounded-lg bg-teal-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-600"
            >
              {esUltimo ? "Terminar" : "Siguiente"} {!esUltimo && <ArrowRight size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
